import {
  arrow,
  autoUpdate,
  flip,
  FloatingArrow,
  offset,
  Placement,
  shift,
  useClick,
  useDismiss,
  useFloating,
  useInteractions,
  useRole,
} from "@floating-ui/react";
import {
  Button,
  ButtonVariant,
  Card,
} from "@lya-protect/lya-protect-form-library/dist/UI";
import classNames from "classnames";
import { ReactElement, ReactNode, useId, useRef, useState } from "react";
import styles from "./Popover.module.scss";

const ARROW_HEIGHT = 8;
const ARROW_WIDTH = 16;
const GAP = 4;

export type PopoverTriggerProps = {
  ref: (node: HTMLElement | null) => void;
  isOpen: boolean;
} & Record<string, unknown>;

export type PopoverProps = {
  children: ReactNode | ((props: { close: () => void }) => ReactNode);
  renderTrigger?: (props: PopoverTriggerProps) => ReactNode;
  content?: ReactElement | string;
  icon?: ReactElement;
  iconVariant?: "top" | "left" | "right";
  variant?: ButtonVariant;
  placement?: Placement;
  className?: string;
  cardClassName?: string;
  ariaLabel?: string;
};

export function Popover({
  children,
  renderTrigger,
  content,
  icon,
  iconVariant,
  variant = "outline",
  placement = "bottom",
  className,
  cardClassName,
  ariaLabel,
}: PopoverProps) {
  const [isOpen, setIsOpen] = useState(false);
  const arrowRef = useRef(null);

  const { refs, floatingStyles, context } = useFloating({
    placement,
    open: isOpen,
    onOpenChange: setIsOpen,
    middleware: [
      offset(ARROW_HEIGHT + GAP),
      flip({ fallbackAxisSideDirection: "end" }),
      shift({ padding: 8 }),
      arrow({ element: arrowRef }),
    ],
    whileElementsMounted: autoUpdate,
  });

  const click = useClick(context);
  const role = useRole(context);
  const dismiss = useDismiss(context);

  const { getReferenceProps, getFloatingProps } = useInteractions([
    click,
    role,
    dismiss,
  ]);

  const close = () => setIsOpen(false);
  const headingId = useId();

  return (
    <>
      {renderTrigger ? (
        renderTrigger({
          ref: refs.setReference,
          isOpen,
          ...getReferenceProps(),
        })
      ) : (
        <Button
          ref={refs.setReference}
          {...getReferenceProps()}
          ariaLabel={ariaLabel}
          variant={variant}
          icon={icon}
          iconVariant={iconVariant}
          className={classNames(
            styles.trigger,
            className,
            isOpen && styles.triggerOpen
          )}
        >
          {content}
        </Button>
      )}
      {isOpen && (
        <div
          className={styles.floating}
          ref={refs.setFloating}
          style={{ ...floatingStyles, zIndex: 1 }}
          aria-labelledby={headingId}
          {...getFloatingProps()}
        >
          <FloatingArrow
            ref={arrowRef}
            context={context}
            className={styles.arrow}
            width={ARROW_WIDTH}
            height={ARROW_HEIGHT}
            strokeWidth={1}
          />
          <Card className={classNames(styles.card, cardClassName)}>
            {typeof children === "function" ? children({ close }) : children}
          </Card>
        </div>
      )}
    </>
  );
}
